import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Trash2, AlertTriangle } from "lucide-react";
import { PLACEMENTS, placementShortcut } from "@/lib/liveentry-helpers";

export function EntryRow({ row, index, photogs, photogByCode, comp, canEdit, onChange, onSave, onRemove, onAdvance }) {
  const codeRef = useRef(null);
  const nameRef = useRef(null);
  const titleRef = useRef(null);
  const scoreRef = useRef(null);
  const placementRef = useRef(null);

  const [acOpen, setAcOpen] = useState(false);
  const [acIdx, setAcIdx] = useState(0);
  const [acRect, setAcRect] = useState(null);
  const [codeInput, setCodeInput] = useState(row.photographer_code || "");
  const [nameAcOpen, setNameAcOpen] = useState(false);
  const [nameAcIdx, setNameAcIdx] = useState(0);
  const [nameAcRect, setNameAcRect] = useState(null);
  const [nameInput, setNameInput] = useState(row.photographer_name || "");

  // Compute fixed-position rect from the input so the portal dropdown
  // can sit outside the (overflow-clipped) table container.
  const measureRect = (el) => {
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const flipUp = spaceBelow < 260;
    const maxH = flipUp ? Math.max(120, r.top - 12) : Math.max(120, spaceBelow - 12);
    return { left: r.left, width: r.width, top: r.bottom, bottom: r.top, flipUp, maxH };
  };

  useEffect(() => {
    if (!acOpen) return;
    const update = () => setAcRect(measureRect(codeRef.current));
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [acOpen]);

  useEffect(() => {
    if (!nameAcOpen) return;
    const update = () => setNameAcRect(measureRect(nameRef.current));
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [nameAcOpen]);

  useEffect(() => {
    setCodeInput(row.photographer_code || "");
  }, [row.photographer_code]);

  useEffect(() => {
    setNameInput(row.photographer_name || "");
  }, [row.photographer_name]);

  const suggestions = useMemo(() => {
    const q = (codeInput || "").trim().toUpperCase();
    if (!q) return [];
    return photogs
      .filter(
        (p) =>
          p.photographer_code.toUpperCase().includes(q) ||
          p.display_name.toUpperCase().includes(q) ||
          p.last_name.toUpperCase().includes(q)
      )
      .slice(0, 8);
  }, [codeInput, photogs]);

  const nameSuggestions = useMemo(() => {
    const q = (nameInput || "").trim().toUpperCase();
    if (!q) return [];
    return photogs
      .filter(
        (p) =>
          p.display_name.toUpperCase().includes(q) ||
          p.last_name.toUpperCase().includes(q) ||
          p.first_name.toUpperCase().includes(q) ||
          p.photographer_code.toUpperCase().includes(q)
      )
      .slice(0, 8);
  }, [nameInput, photogs]);

  // Find a photographer matching the typed text by code OR name.
  const resolveTypedPhotographer = () => {
    const raw = (codeInput || "").trim();
    if (!raw) return null;
    const q = raw.toUpperCase();
    const byCode = photogByCode[q];
    if (byCode) return byCode;
    const exactName = photogs.find(
      (p) =>
        p.display_name.toUpperCase() === q ||
        `${p.first_name} ${p.last_name}`.toUpperCase() === q ||
        `${p.last_name} ${p.first_name}`.toUpperCase() === q ||
        p.last_name.toUpperCase() === q
    );
    if (exactName) return exactName;
    if (suggestions.length === 1) return suggestions[0];
    return null;
  };

  const resolveTypedName = () => {
    const raw = (nameInput || "").trim();
    if (!raw) return null;
    const q = raw.toUpperCase();
    const exact = photogs.find(
      (p) =>
        p.display_name.toUpperCase() === q ||
        `${p.first_name} ${p.last_name}`.toUpperCase() === q ||
        `${p.last_name} ${p.first_name}`.toUpperCase() === q ||
        p.last_name.toUpperCase() === q ||
        p.first_name.toUpperCase() === q
    );
    if (exact) return exact;
    if (nameSuggestions.length === 1) return nameSuggestions[0];
    return null;
  };

  const pickPhotographer = (p) => {
    onChange({
      photographer_id: p.id,
      photographer_code: p.photographer_code,
      photographer_name: p.display_name,
      status: row.score !== "" && row.image_title ? "dirty" : (row.status === "saved" ? "dirty" : "empty"),
    });
    setCodeInput(p.photographer_code);
    setNameInput(p.display_name);
    setAcOpen(false);
    setNameAcOpen(false);
    setTimeout(() => titleRef.current?.focus(), 0);
  };

  const onCodeKeyDown = (e) => {
    if (acOpen && suggestions.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setAcIdx((i) => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setAcIdx((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter") {
        e.preventDefault();
        pickPhotographer(suggestions[acIdx]);
        return;
      }
      if (e.key === "Tab") {
        pickPhotographer(suggestions[acIdx]);
        return;
      }
      if (e.key === "Escape") { setAcOpen(false); return; }
    } else if (e.key === "Enter") {
      const match = resolveTypedPhotographer();
      if (match) {
        e.preventDefault();
        pickPhotographer(match);
      }
    }
  };

  const onCodeBlur = () => {
    const match = resolveTypedPhotographer();
    if (match && match.id !== row.photographer_id) {
      onChange({
        photographer_id: match.id,
        photographer_code: match.photographer_code,
        photographer_name: match.display_name,
        status: row.status === "saved" ? "dirty" : row.status,
      });
      setCodeInput(match.photographer_code);
    } else if (!match && codeInput) {
      onChange({ photographer_id: "", photographer_name: "" });
    }
    setTimeout(() => setAcOpen(false), 120);
  };

  const onNameKeyDown = (e) => {
    if (nameAcOpen && nameSuggestions.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setNameAcIdx((i) => Math.min(i + 1, nameSuggestions.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setNameAcIdx((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter") {
        e.preventDefault();
        pickPhotographer(nameSuggestions[nameAcIdx]);
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        pickPhotographer(nameSuggestions[nameAcIdx]);
        return;
      }
      if (e.key === "Escape") { setNameAcOpen(false); return; }
    } else if (e.key === "Enter") {
      const match = resolveTypedName();
      if (match) {
        e.preventDefault();
        pickPhotographer(match);
      }
    }
  };

  const onNameBlur = () => {
    const match = resolveTypedName();
    if (match && match.id !== row.photographer_id) {
      onChange({
        photographer_id: match.id,
        photographer_code: match.photographer_code,
        photographer_name: match.display_name,
        status: row.status === "saved" ? "dirty" : row.status,
      });
      setNameInput(match.display_name);
    } else if (!match && nameInput && !row.photographer_id) {
      onChange({ photographer_id: "", photographer_code: "" });
    }
    setTimeout(() => setNameAcOpen(false), 120);
  };

  const onTitleChange = (v) => {
    onChange({ image_title: v, status: row.status === "saved" ? "dirty" : (v ? "dirty" : "empty") });
  };

  const onScoreChange = (v) => {
    onChange({ score: v, status: row.status === "saved" ? "dirty" : (v ? "dirty" : "empty") });
  };

  const onScoreKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSave();
      onAdvance();
      // Jump to the score cell of the next row (rather than the I.D. cell)
      // — clubs that pre-populate the photographer list and only fill in
      // scores after the competition asked for this in Feb 2026.
      setTimeout(() => {
        const next = e.target.closest("tr").nextElementSibling;
        const target =
          next?.querySelector('input[data-cell="score"]') ||
          next?.querySelector('input[data-cell="code"]');
        target?.focus();
        // Highlight the score for quick overwrite.
        if (target && target.select) target.select();
      }, 30);
    } else if (e.key === "Tab" && !e.shiftKey) {
      // Tab hops Title → Score → next-row Title so the operator can flow
      // through titles and scores in one pass without ever landing on
      // Placement or the delete button.
      const next = e.target.closest("tr").nextElementSibling;
      const target = next?.querySelector('input[data-cell="title"]');
      if (target) {
        e.preventDefault();
        onSave();
        onAdvance();
        setTimeout(() => { target.focus(); target.select?.(); }, 20);
      }
    }
  };

  const onPlacementKeyDown = (e) => {
    const mapped = placementShortcut(e.key);
    if (mapped) {
      e.preventDefault();
      onChange({ placement: mapped, status: row.status === "saved" ? "dirty" : row.status });
      setTimeout(() => {
        // Pass explicit override — parent's onSave closure still sees
        // the OLD row after the 10ms delay because React batching
        // hasn't flushed our updateRow(setRows) into a re-render yet.
        onSave({ placement: mapped });
        onAdvance();
        const next = e.target.closest("tr").nextElementSibling;
        const target =
          next?.querySelector('input[data-cell="score"]') ||
          next?.querySelector('input[data-cell="code"]');
        target?.focus();
        if (target && target.select) target.select();
      }, 10);
    } else if (e.key === "Enter") {
      e.preventDefault();
      onSave();
      onAdvance();
      const next = e.target.closest("tr").nextElementSibling;
      const target =
        next?.querySelector('input[data-cell="score"]') ||
        next?.querySelector('input[data-cell="code"]');
      setTimeout(() => {
        target?.focus();
        if (target && target.select) target.select();
      }, 30);
    }
  };

  const rowClass =
    row.status === "saved" ? "row-saved" :
    row.status === "error" ? "row-error" :
    row.status === "dirty" ? "row-dirty" : "";

  return (
    <tr className={rowClass} data-testid={`entry-row-${index}`} data-status={row.status}>
      <td className="mono muted-text text-center" style={{ fontSize: 13 }}>
        {(index + 1).toString().padStart(2, "0")}
      </td>

      <td className="cell-mono" style={{ position: "relative" }}>
        <input
          ref={codeRef}
          data-cell="code"
          data-testid={`row-${index}-code`}
          value={codeInput}
          disabled={!canEdit}
          onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setAcOpen(true); setAcIdx(0); }}
          onFocus={() => setAcOpen(true)}
          onBlur={onCodeBlur}
          onKeyDown={onCodeKeyDown}
          placeholder="SMI001"
        />
        {acOpen && suggestions.length > 0 && acRect && createPortal(
          <div
            className="autocomplete-dropdown"
            data-testid={`row-${index}-autocomplete`}
            style={{
              position: "fixed",
              left: acRect.left,
              width: acRect.width,
              maxHeight: acRect.maxH,
              ...(acRect.flipUp
                ? { bottom: window.innerHeight - acRect.bottom + 1, top: "auto" }
                : { top: acRect.top + 1, bottom: "auto" }),
            }}
          >
            {suggestions.map((s, i) => (
              <div
                key={s.id}
                className={"autocomplete-item " + (i === acIdx ? "active" : "")}
                onMouseDown={(e) => { e.preventDefault(); pickPhotographer(s); }}
              >
                <span className="code">{s.photographer_code}</span>
                <span className="name">{s.display_name}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
      </td>

      <td className="cell-name" style={{ position: "relative" }}>
        <input
          ref={nameRef}
          data-cell="name"
          data-testid={`row-${index}-name`}
          value={nameInput}
          disabled={!canEdit}
          onChange={(e) => { setNameInput(e.target.value); setNameAcOpen(true); setNameAcIdx(0); }}
          onFocus={() => setNameAcOpen(true)}
          onBlur={onNameBlur}
          onKeyDown={onNameKeyDown}
          placeholder="Photographer name"
        />
        {nameAcOpen && nameSuggestions.length > 0 && nameAcRect && createPortal(
          <div
            className="autocomplete-dropdown"
            data-testid={`row-${index}-name-autocomplete`}
            style={{
              position: "fixed",
              left: nameAcRect.left,
              width: nameAcRect.width,
              maxHeight: nameAcRect.maxH,
              ...(nameAcRect.flipUp
                ? { bottom: window.innerHeight - nameAcRect.bottom + 1, top: "auto" }
                : { top: nameAcRect.top + 1, bottom: "auto" }),
            }}
          >
            {nameSuggestions.map((s, i) => (
              <div
                key={s.id}
                className={"autocomplete-item " + (i === nameAcIdx ? "active" : "")}
                onMouseDown={(e) => { e.preventDefault(); pickPhotographer(s); }}
              >
                <span className="code">{s.photographer_code}</span>
                <span className="name">{s.display_name}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
      </td>

      <td>
        <input
          ref={titleRef}
          data-testid={`row-${index}-title`}
          data-cell="title"
          value={row.image_title}
          disabled={!canEdit}
          onChange={(e) => onTitleChange(e.target.value)}
          onBlur={() => {
            // Persist the row when the user finishes typing the title,
            // even if no score has been entered yet. Without this, titles
            // stayed only in client memory and never reached the DB —
            // so print sheets for unscored competitions came out blank.
            if (row.photographer_id) onSave();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              scoreRef.current?.focus();
              scoreRef.current?.select?.();
            } else if (e.key === "Tab" && e.shiftKey) {
              // Shift+Tab jumps back to the previous row's score,
              // completing the Title → Score → next-row Title flow.
              const prev = e.target.closest("tr").previousElementSibling;
              const target = prev?.querySelector('input[data-cell="score"]');
              if (target) { e.preventDefault(); target.focus(); target.select?.(); }
            }
          }}
          placeholder="Image title"
        />
        {row.duplicateWarn && (
          <div className="flex items-center gap-1 text-amber-400 text-xs px-3 pb-1">
            <AlertTriangle size={12} /> duplicate title in this competition
          </div>
        )}
      </td>

      <td className="cell-score">
        <input
          ref={scoreRef}
          data-testid={`row-${index}-score`}
          data-cell="score"
          type="number"
          min={0}
          max={999}
          step="0.5"
          value={row.score}
          disabled={!canEdit}
          onChange={(e) => onScoreChange(e.target.value)}
          onKeyDown={onScoreKeyDown}
          placeholder="0"
        />
      </td>

      <td>
        <select
          ref={placementRef}
          data-testid={`row-${index}-placement`}
          value={row.placement}
          disabled={!canEdit}
          onChange={(e) => {
            // Fix Feb 2026 (v2): pass placement as explicit override to
            // onSave to defeat the stale-closure that would otherwise
            // send the OLD placement value. updateRow is async (setState);
            // saveRow needs the fresh value NOW, not after re-render.
            const newPlacement = e.target.value;
            onChange({ placement: newPlacement, status: row.status === "saved" ? "dirty" : row.status });
            if (row.photographer_id) onSave({ placement: newPlacement });
          }}
          onKeyDown={onPlacementKeyDown}
          className="ccs-input-mono"
        >
          {PLACEMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {row.status === "error" && (
          <div className="danger-text text-xs px-3 pb-1">{row.error}</div>
        )}
      </td>

      <td className="text-center">
        {canEdit && (
          <button
            onClick={onRemove}
            className="p-2 text-red-400 hover:bg-neutral-800"
            title="Delete row"
            data-testid={`row-${index}-delete`}
          >
            <Trash2 size={14} />
          </button>
        )}
      </td>
    </tr>
  );
}
